import { DateTime } from 'luxon';
import { isDefined, isUndefined, thenElse } from 'dg-web-shared/lib/MaybeV2.ts';
import { Translation } from '../../common/i18n/Text.ts';
import * as PermitCreateTexts from '../i18n/PermitCreateTexts.ts';
import * as PermitCreateState from '../state/PermitCreateState.ts';
import * as PermitTypeState from '../../common/state/PermitTypeState.ts';
import { Store, useStore } from 'dg-web-shared/lib/Flux.tsx';
import { DatePickerSlideIn } from '../../ui/slidein/DatePickerSlideIn.tsx';
import { useLanguage } from 'dg-web-shared/lib/Localization.tsx';

export interface Texts {
    ToCaption: Translation;
}

export function PermitCreateValidityToDateSlideIn() {
    const { storeState, update } = useStore(store => ({
        layout: PermitCreateState.Layout.get(store),
        config: PermitCreateState.Config.get(store),
        permitType: PermitTypeState.getById(
            store,
            PermitCreateState.Config.get(store).permitTypeId,
        ),
    }));
    const { language } = useLanguage();

    function txt(): Texts {
        return PermitCreateTexts.validityToDate[language];
    }

    function onDateSelected(dateField: string, selectedDate: DateTime): void {
        update((store: Store): string => {
            PermitCreateState.Config.stateWrite(store, {
                [dateField]: selectedDate,
            });
            PermitCreateState.Layout.stateWrite(store, {
                validityToDatePickerOpen: false,
            });
            PermitCreateState.Calc.reset(store);
            return 'PermitCreateValidityToDateSlideIn-selectDate';
        });
    }

    const permitType = storeState.permitType;

    if (isUndefined(permitType)) {
        return null;
    } else {
        if (isUndefined(permitType.toRange)) {
            return null;
        } else {
            const configMinDate = thenElse(
                permitType.toRange,
                range => range.start,
                null,
            );

            // don't allow selection of date before selected start date
            const minDate = thenElse(
                storeState.config.startDate,
                startDate =>
                    configMinDate
                        ? startDate > configMinDate
                            ? startDate
                            : configMinDate
                        : startDate,
                configMinDate,
            );
            const configMaxDate = thenElse(
                permitType.toRange,
                range =>
                    minDate == null
                        ? range.end
                        : minDate.plus({ years: 2 }).minus({ days: 1 }) <
                            range.end
                          ? minDate.plus({ years: 2 }).minus({ days: 1 })
                          : range.end,
                null,
            );

            return (
                <DatePickerSlideIn
                    open={storeState.layout.validityToDatePickerOpen}
                    canSelectMaxDate={isDefined(configMaxDate)}
                    caption={txt().ToCaption()}
                    onClose={() =>
                        update(store =>
                            PermitCreateState.Layout.stateWrite(store, {
                                validityToDatePickerOpen: false,
                            }),
                        )
                    }
                    selectedDate={storeState.config.endDate}
                    onSelect={(selectedDate: DateTime) => {
                        return onDateSelected('endDate', selectedDate);
                    }}
                    minDate={minDate}
                    maxDate={configMaxDate}
                    language={language}
                    outsideBody
                />
            );
        }
    }
}
